import React, { useState, useEffect } from "react";
import {
  Typography,
  Button,
  Dialog,
  DialogContent,
  TextField,
  useTheme,
  InputAdornment,
  IconButton,
  styled,
  CircularProgress,
  Box,
  Stack,
  Tooltip,
} from "@mui/material";
import ReactHtmlParser from "react-html-parser";
import { Email, Lock } from "@mui/icons-material";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import {
  loginForm,
  loginGoogle,
  setAuthenticated,
  setOpenLogin,
  setSnackbarOpen,
  emailCheckRegister,
  sendForgotPassword,
  loginV2,
  verifyMfa,
  getCaptcha,
  setMiddlewareApps,
} from "src/redux/actions";
import { useNavigate } from "react-router";
import { FcGoogle } from "react-icons/fc";
import localforage from "localforage";
import { version } from "src/configs/globalVariable";
import { domain } from "./Layout/dummy";
import OTP from "./OTP";
import CustomInput from "./CustomInput";
import { Refresh } from "@material-ui/icons";

const CustomDialog = styled(Dialog)(() => ({
  "& .MuiDialog-paper": {
    backgroundColor: "rgb(255,255,255)",
    borderRadius: "1rem",
    border: "1px solid lightgrey",
    maxWidth: "450px",
  },
}));

const CustomCircularProgress = styled(CircularProgress)(
  ({ theme, loading }) => ({
    "&.MuiCircularProgress-root": {
      height: "20px !important",
      width: "20px !important",
      display: "flex",
      color: loading === "google" ? `${theme.palette.primary.main}` : "#ffff",
      marginRight: loading === "google" ? "0.5rem !important" : null,
    },
  }),
);

const CustomTextField = styled(TextField)(({ theme }) => ({
  "&.Mui-focused": {
    border: `0.1px solid ${theme.palette.primary.main}`,
  },
  "&:hover fieldset": {
    border: `0.1px solid ${theme.palette.primary.main} !important`,
  },
}));

const Login = ({ open, onClose }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPass, setShowPass] = useState(false);
  const [getAppSetting, setAppSetting] = useState({});
  const [loading, setLoading] = useState(false);
  const [validate, setValidate] = useState(false);
  const [isRegist, setIsRegist] = useState(false);
  const [isForgotPass, setIsForgotPass] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const [otp, setOtp] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [resSvg, setResSvg] = useState("");

  useEffect(() => {
    fetchInfo();
    setValidate(false);
    setLoading(false);
    setIsRegist(false);
    setIsForgotPass(false);
    handleGetCaptcha();
  }, [open]);

  const fetchInfo = async () => {
    const resTheme = await localforage.getItem("theme");
    setAppSetting(resTheme);
  };

  const handleGetCaptcha = async () => {
    const resCaptcha = await getCaptcha();
    setForm({ ...form, captchaId: resCaptcha?.data?.id });
    setResSvg(resCaptcha?.data?.svg);
  };

  const handleChange = (e) => {
    const copyForm = { ...form };
    copyForm[e.target.name] = e.target.value;
    setForm(copyForm);
  };

  const login = async () => {
    if (!form.email || !form.password) {
      setValidate(true);
      return;
    }
    setLoading("login");
    // const resLogin = await loginForm(form);
    const resLogin = await loginV2(form);

    if (resLogin.code === 200) {
      if (resLogin?.data?.userStatus === "1") {
        setOpenLogin(false);
        setMiddlewareApps(true, resLogin?.data?.userRoles, form, resLogin);
      } else {
        setTempToken(resLogin?.data?.tempToken);
        setOtpOpen(true);
      }
    } else if (resLogin.code === 400 || resLogin.code === 403) {
      setSnackbarOpen("error", resLogin.message);
      if (
        resLogin?.message ===
        "Password Anda telah kedaluwarsa. Silakan reset password."
      ) {
        setIsForgotPass(true);
      }
    }
    setLoading(false);
  };

  const COMMON_EMAIL_DOMAINS = [
    "gmail.com",
    "yahoo.com",
    "yahoo.co.id",
    "outlook.com",
    "hotmail.com",
    "live.com",
    "icloud.com",
    "proton.me",
    "protonmail.com",
  ];

  function levenshteinDistance(a, b) {
    const matrix = Array.from({ length: b.length + 1 }, () =>
      Array(a.length + 1).fill(0),
    );

    for (let i = 0; i <= b.length; i++) {
      matrix[i][0] = i;
    }

    for (let j = 0; j <= a.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        const cost = b[i - 1] === a[j - 1] ? 0 : 1;

        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + cost,
        );
      }
    }

    return matrix[b.length][a.length];
  }

  function isValidEmail(email) {
    if (!email || typeof email !== "string") {
      return false;
    }

    email = email.trim().toLowerCase();

    // Format email
    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!emailPattern.test(email)) {
      return false;
    }

    const atIndex = email.lastIndexOf("@");

    const localPart = email.substring(0, atIndex);
    const domain = email.substring(atIndex + 1);

    // Validasi local part
    if (
      !localPart ||
      localPart.startsWith(".") ||
      localPart.endsWith(".") ||
      localPart.includes("..")
    ) {
      return false;
    }

    // Validasi domain
    if (
      !domain ||
      domain.startsWith(".") ||
      domain.endsWith(".") ||
      domain.startsWith("-") ||
      domain.endsWith("-") ||
      domain.includes("..")
    ) {
      return false;
    }

    // Domain yang benar
    if (COMMON_EMAIL_DOMAINS.includes(domain)) {
      return true;
    }

    // Cek kemungkinan typo
    for (const commonDomain of COMMON_EMAIL_DOMAINS) {
      const distance = levenshteinDistance(domain, commonDomain);

      // Untuk domain populer, 1 karakter salah dianggap typo
      if (distance <= 2) {
        return false;
      }
    }

    // Domain perusahaan/custom tetap diperbolehkan
    return true;
  }

  // function isValidEmail(email) {
  //   // Ekspresi reguler untuk memeriksa validitas alamat email
  //   const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

  //   return emailPattern.test(email);
  // }

  const register = async () => {
    console.log(form.email);
    console.log(isValidEmail(form.email));
    if (!isValidEmail(form.email)) {
      setSnackbarOpen("error", "Email tidak valid");
      return;
    }

    setLoading("login");
    const resLogin = await emailCheckRegister({ email: form.email });
    setLoading(false);

    if (resLogin.code === 409) {
      setSnackbarOpen("error", resLogin.message);
      return;
    }

    if (resLogin.code === 200) {
      await localforage.setItem("email", form.email);
      setOpenLogin(false);
      navigate(`/${version}/register`);
      return;
    }
  };

  const handleGoogle = async () => {
    setLoading("google");
    const resGoogle = await loginGoogle();

    if (resGoogle.code === 200) {
      setOpenLogin(false);
      setAuthenticated(resGoogle.data.token);
      navigate(`/${version}/user/`);
      setTimeout(() => {
        window.location.reload();
      }, "500");
    } else if (resGoogle.code === 400 || resGoogle.code === 403) {
      setSnackbarOpen("error", resGoogle.message);
    } else if (resGoogle.status === 403) {
      setOpenLogin(false);
      navigate(`/${version}/register`);
      return;
    }
    setLoading(false);
  };

  const handleSendForgot = async () => {
    form.domain = domain;
    setLoading("reset-password");
    await sendForgotPassword(form);
    setSnackbarOpen("success", "Silahkan cek email anda");
    setIsForgotPass(false);
    setOpenLogin(false);
  };

  const handleSubmitOtp = async () => {
    setLoading("otp");
    const verif = await verifyMfa({
      email: form.email,
      otp: otp,
      tempToken: tempToken,
    });
    setLoading("");
    if (verif?.code === 200) {
      setOtpOpen(false);
      setOpenLogin(false);
      setAuthenticated(verif?.data?.token);
      navigate(`/${version}/user/`);
      setTimeout(() => {
        window.location.reload();
      }, "500");
    } else if (verif?.code === 400 || verif?.code === 403) {
      setSnackbarOpen("error", verif.message);
    }
  };

  return (
    <CustomDialog open={open} onClose={onClose}>
      {otpOpen ? (
        <DialogContent>
          <Typography variant="h4" sx={{ mb: 2 }}>
            Verifikasi OTP
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Kami telah mengirimkan kode OTP ke email Anda. Silakan masukkan kode
            tersebut untuk melanjutkan proses login.
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              alignItems: "center",
            }}
          >
            <OTP
              separator={<span>-</span>}
              value={otp}
              onChange={setOtp}
              length={6}
            />
          </Box>
          <Button
            variant="contained"
            size="medium"
            color="primary"
            fullWidth
            onClick={loading === "otp" ? null : handleSubmitOtp}
            sx={{
              fontWeight: 700,
              borderRadius: "5rem",
              transition: "all linear .4s",
              mt: 3,
              "&:hover": {
                transform: "scale(1.05)",
              },
            }}
          >
            {loading === "otp" ? <CustomCircularProgress /> : "Submit"}
          </Button>
          <Typography
            sx={{
              fontSize: "14px",
              textAlign: "center",
              mb: 1,
              mt: 2,
              cursor: "pointer",
              color: "#3f51b5",
              textDecoration: "underline",
            }}
            onClick={() => setOtpOpen(false)}
          >
            Kembali ke Login
          </Typography>
        </DialogContent>
      ) : (
        <DialogContent>
          <Typography variant="h4" sx={{ mb: 4 }}>
            {isRegist ? "Daftar" : isForgotPass ? "Reset Password" : "Login"}
          </Typography>
          <CustomTextField
            placeholder="Email"
            type="email"
            name="email"
            variant="outlined"
            color="primary"
            hiddenLabel
            fullWidth
            error={validate && !form.email}
            helperText={validate && !form.email && "* email harap diisi"}
            value={form.email}
            onChange={handleChange}
            size="small"
            InputProps={{
              startAdornment: (
                <Email sx={{ color: `${theme.palette.primary.main}`, mr: 1 }} />
              ),
            }}
          />
          {!isRegist && !isForgotPass && (
            <>
              <CustomTextField
                placeholder="Kata sandi"
                type={showPass ? "text" : "password"}
                variant="outlined"
                color="primary"
                name="password"
                hiddenLabel
                fullWidth
                error={validate && !form.password}
                helperText={
                  validate && !form.password && "* Kata sandi harap diisi"
                }
                sx={{ mt: 2 }}
                value={form.password}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <Lock
                      sx={{ color: `${theme.palette.primary.main}`, mr: 1 }}
                    />
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPass(!showPass)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showPass ? (
                          <Visibility color="primary" />
                        ) : (
                          <VisibilityOff />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
              <Typography
                sx={{
                  fontSize: "14px",
                  textAlign: "right",
                  mb: 1,
                  mt: 1,
                  cursor: "pointer",
                  color: "#3f51b5",
                }}
                onClick={() => setIsForgotPass(true)}
              >
                Lupa kata sandi?
              </Typography>
              <Stack direction="row" spacing={2}>
                <Stack sx={{ width: "100%", position: "relative" }}>
                  {resSvg && ReactHtmlParser(resSvg)}
                  <Stack sx={{ position: "absolute", right: "12px" }}>
                    <Tooltip title="Refresh Captcha">
                      <IconButton onClick={handleGetCaptcha}>
                        <Refresh />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
                <CustomInput
                  label=""
                  name="captcha"
                  value={form.captcha}
                  onChange={(e) =>
                    setForm({ ...form, captcha: e.target.value })
                  }
                  placeholder="Input Captcha"
                  style={{ margin: "auto" }}
                />
              </Stack>
              <Button
                variant="contained"
                size="medium"
                color="primary"
                fullWidth
                onClick={loading === "login" ? null : login}
                sx={{
                  fontWeight: 700,
                  borderRadius: "5rem",
                  transition: "all linear .4s",
                  mt: 3,
                  "&:hover": {
                    transform: "scale(1.05)",
                  },
                }}
              >
                {loading === "login" ? <CustomCircularProgress /> : "Login"}
              </Button>
              <Button
                variant="outlined"
                size="medium"
                color="primary"
                fullWidth
                onClick={loading === "google" ? null : handleGoogle}
                sx={{
                  mt: 2,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "5rem",
                  transition: "all linear .4s",
                  fontWeight: 600,
                  "&:hover": {
                    transform: "scale(1.05)",
                  },
                }}
              >
                {loading === "google" ? (
                  <CustomCircularProgress loading="google" />
                ) : (
                  <FcGoogle
                    style={{
                      height: "40px !important",
                      marginRight: "0.5rem",
                    }}
                  />
                )}

                <Typography variant="span">Daftar / Masuk</Typography>
              </Button>
              <Typography
                sx={{ fontSize: "14px", textAlign: "center", mb: "1rem" }}
              >
                Belum memiliki akun?{" "}
                <em
                  style={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    color: "#3f51b5",
                  }}
                  onClick={() => setIsRegist(true)}
                >
                  Daftar
                </em>
              </Typography>
            </>
          )}
          {isRegist && (
            <>
              <Button
                variant="contained"
                size="medium"
                color="primary"
                fullWidth
                onClick={loading === "login" ? null : register}
                sx={{
                  fontWeight: 700,
                  borderRadius: "5rem",
                  transition: "all linear .4s",
                  mt: 3,
                  mb: 2,
                  "&:hover": {
                    transform: "scale(1.05)",
                  },
                }}
              >
                {loading === "login" ? <CustomCircularProgress /> : "Daftar"}
              </Button>
              <Typography
                sx={{ fontSize: "14px", textAlign: "center", mb: "1rem" }}
              >
                Sudah memiliki akun?{" "}
                <em
                  style={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    color: "#3f51b5",
                  }}
                  onClick={() => setIsRegist(false)}
                >
                  Login
                </em>
              </Typography>
            </>
          )}

          {isForgotPass && (
            <>
              <Button
                variant="contained"
                size="medium"
                color="primary"
                fullWidth
                onClick={loading === "reset-password" ? null : handleSendForgot}
                sx={{
                  fontWeight: 700,
                  borderRadius: "5rem",
                  transition: "all linear .4s",
                  mt: 3,
                  mb: 2,
                  "&:hover": {
                    transform: "scale(1.05)",
                  },
                }}
              >
                {loading === "reset-password" ? (
                  <CustomCircularProgress />
                ) : (
                  "Reset Password"
                )}
              </Button>
              <Typography
                sx={{
                  fontSize: "14px",
                  textAlign: "center",
                  mb: "1rem",
                  cursor: "pointer",
                  color: "#3f51b5",
                }}
                onClick={() => setIsForgotPass(false)}
              >
                Kembali
              </Typography>
            </>
          )}
        </DialogContent>
      )}
    </CustomDialog>
  );
};

export default Login;
